import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Shield, Users, Award, Clock, CheckCircle2, Target, ArrowRight } from 'lucide-react';
import CountUp from '../components/CountUp';
import RegistrationModal from '../components/RegistrationModal';
import Slider from 'react-slick';

const AboutPage = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  return (
    <div className="min-h-screen bg-slate-950">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-[#fd5a27]/20 to-transparent"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center space-x-2 bg-slate-800/50 backdrop-blur-sm px-4 py-2 rounded-full shadow-sm border border-[#fd5a27]/20 mb-6">
            <Shield className="w-5 h-5 text-[#fd5a27]" />
            <span className="text-sm font-medium text-gray-200">Trusted Since 2015</span>
          </div>
          <h1 className="text-5xl lg:text-6xl font-bold text-white mb-6">
            About Trademark IP US
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Your trusted partner for trademark registration and intellectual property protection, delivering excellence and reliability in brand protection services
          </p>
        </div>
      </section>

      {/* Our Story Section */}
      <section className="py-20 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-white mb-6">
                Our Vision: Securing Your Success!
              </h2>
              <p className="text-gray-300 mb-6 leading-relaxed">
                At Trademark IP US, we are dedicated to protecting your brand's identity and ensuring its continued growth. With our expert team, we focus on identifying and preventing potential conflicts, giving you the peace of mind to thrive.
              </p>
              <p className="text-gray-300 mb-8 leading-relaxed">
                From securing logos to safeguarding entire brands, we provide the protection you need for lasting success. Trust us to make your brand our priority.
              </p>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="flex items-start bg-slate-800/30 p-4 rounded-lg border border-slate-700/50 hover:border-[#fd5a27]/50 transition-all duration-300 hover:bg-slate-800/50">
                  <Users className="w-6 h-6 text-[#fd5a27] mr-3 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-bold text-white mb-1">Expert Team</h3>
                    <p className="text-sm text-gray-300">Experienced professionals at your service</p>
                  </div>
                </div>
                <div className="flex items-start bg-slate-800/30 p-4 rounded-lg border border-slate-700/50 hover:border-[#fd5a27]/50 transition-all duration-300 hover:bg-slate-800/50">
                  <Shield className="w-6 h-6 text-[#fd5a27] mr-3 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-bold text-white mb-1">Full Protection</h3>
                    <p className="text-sm text-gray-300">Comprehensive IP security solutions</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="rounded-xl overflow-hidden shadow-2xl shadow-[#fd5a27]/20 border border-slate-700 hover:shadow-[#fd5a27]/40 transition-all duration-300 hover:scale-[1.02]">
              <img
                src="https://customer-assets.emergentagent.com/job_brand-registry-2/artifacts/6fg5cpq6_img-1.jpg"
                alt="Team collaboration"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-slate-800/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-white mb-4">
              Why Choose Trademark IP US?
            </h2>
            <p className="text-gray-300 max-w-2xl mx-auto">
              We combine legal expertise with exceptional customer service to deliver results you can trust.
            </p>
          </div>
          
          <div className="md:hidden">
            <Slider
              className="home-card-slider"
              dots={true}
              infinite={true}
              speed={500}
              slidesToShow={1}
              slidesToScroll={1}
              autoplay={true}
              autoplaySpeed={3000}
            >
              <div className="px-2">
                <div className="bg-slate-900 p-8 rounded-xl border border-slate-700 hover:border-[#fd5a27]/50 transition-all duration-300 hover:shadow-lg hover:shadow-[#fd5a27]/20 hover:-translate-y-1 group">
                  <div className="w-14 h-14 bg-[#fd5a27]/10 rounded-lg flex items-center justify-center mb-4 group-hover:bg-[#fd5a27]/20 transition-all duration-300">
                    <Shield className="w-7 h-7 text-[#fd5a27] group-hover:text-[#fd5a27]/80 transition-colors duration-300" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3 group-hover:text-[#fd5a27] transition-colors duration-300">Expert Protection</h3>
                  <p className="text-gray-300">
                    Our team of experienced IP attorneys ensures your brand receives the highest level of legal protection with meticulous attention to detail.
                  </p>
                </div>
              </div>
              
              <div className="px-2">
                <div className="bg-slate-900 p-8 rounded-xl border border-slate-700 hover:border-[#fd5a27]/50 transition-all duration-300 hover:shadow-lg hover:shadow-[#fd5a27]/20 hover:-translate-y-1 group">
                  <div className="w-14 h-14 bg-[#fd5a27]/10 rounded-lg flex items-center justify-center mb-4 group-hover:bg-[#fd5a27]/20 transition-all duration-300">
                    <Clock className="w-7 h-7 text-[#fd5a27] group-hover:text-[#fd5a27]/80 transition-colors duration-300" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3 group-hover:text-[#fd5a27] transition-colors duration-300">24-Hour Processing</h3>
                  <p className="text-gray-300">
                    We process your trademark application swiftly within 24 hours, providing fast and efficient service without compromising quality.
                  </p>
                </div>
              </div>
              
              <div className="px-2">
                <div className="bg-slate-900 p-8 rounded-xl border border-slate-700 hover:border-[#fd5a27]/50 transition-all duration-300 hover:shadow-lg hover:shadow-[#fd5a27]/20 hover:-translate-y-1 group">
                  <div className="w-14 h-14 bg-[#fd5a27]/10 rounded-lg flex items-center justify-center mb-4 group-hover:bg-[#fd5a27]/20 transition-all duration-300">
                    <CheckCircle2 className="w-7 h-7 text-[#fd5a27] group-hover:text-[#fd5a27]/80 transition-colors duration-300" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3 group-hover:text-[#fd5a27] transition-colors duration-300">100% Satisfaction</h3>
                  <p className="text-gray-300">
                    We guarantee complete satisfaction with our services and stand behind our work with full confidence and unwavering commitment.
                  </p>
                </div>
              </div>
              
              <div className="px-2">
                <div className="bg-slate-900 p-8 rounded-xl border border-slate-700 hover:border-[#fd5a27]/50 transition-all duration-300 hover:shadow-lg hover:shadow-[#fd5a27]/20 hover:-translate-y-1 group">
                  <div className="w-14 h-14 bg-[#fd5a27]/10 rounded-lg flex items-center justify-center mb-4 group-hover:bg-[#fd5a27]/20 transition-all duration-300">
                    <Users className="w-7 h-7 text-[#fd5a27] group-hover:text-[#fd5a27]/80 transition-colors duration-300" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3 group-hover:text-[#fd5a27] transition-colors duration-300">Dedicated Support</h3>
                  <p className="text-gray-300">
                    24/7 customer support team ready to assist you at every step of your trademark journey with personalized attention.
                  </p>
                </div>
              </div>
              
              <div className="px-2">
                <div className="bg-slate-900 p-8 rounded-xl border border-slate-700 hover:border-[#fd5a27]/50 transition-all duration-300 hover:shadow-lg hover:shadow-[#fd5a27]/20 hover:-translate-y-1 group">
                  <div className="w-14 h-14 bg-[#fd5a27]/10 rounded-lg flex items-center justify-center mb-4 group-hover:bg-[#fd5a27]/20 transition-all duration-300">
                    <Award className="w-7 h-7 text-[#fd5a27] group-hover:text-[#fd5a27]/80 transition-colors duration-300" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3 group-hover:text-[#fd5a27] transition-colors duration-300">Proven Track Record</h3>
                  <p className="text-gray-300">
                    Thousands of successful trademark registrations with a high approval rate and outstanding client satisfaction scores.
                  </p>
                </div>
              </div>
              
              <div className="px-2">
                <div className="bg-slate-900 p-8 rounded-xl border border-slate-700 hover:border-[#fd5a27]/50 transition-all duration-300 hover:shadow-lg hover:shadow-[#fd5a27]/20 hover:-translate-y-1 group">
                  <div className="w-14 h-14 bg-[#fd5a27]/10 rounded-lg flex items-center justify-center mb-4 group-hover:bg-[#fd5a27]/20 transition-all duration-300">
                    <Target className="w-7 h-7 text-[#fd5a27] group-hover:text-white-500 transition-colors duration-300" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3 group-hover:text-[#fd5a27] transition-colors duration-300">Affordable Pricing</h3>
                  <p className="text-gray-300">
                    Competitive rates starting at just $45, making brand protection accessible to businesses of all sizes and budgets.
                  </p>
                </div>
              </div>
            </Slider>
          </div>

          <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-slate-900 p-8 rounded-xl border border-slate-700 hover:border-[#fd5a27]/50 transition-all duration-300 hover:shadow-lg hover:shadow-[#fd5a27]/20 hover:-translate-y-1 group">
              <div className="w-14 h-14 bg-[#fd5a27]/10 rounded-lg flex items-center justify-center mb-4 group-hover:bg-[#fd5a27]/20 transition-all duration-300">
                <Shield className="w-7 h-7 text-[#fd5a27] group-hover:text-[#fd5a27]/80 transition-colors duration-300" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3 group-hover:text-[#fd5a27] transition-colors duration-300">Expert Protection</h3>
              <p className="text-gray-300">
                Our team of experienced IP attorneys ensures your brand receives the highest level of legal protection with meticulous attention to detail.
              </p>
            </div>
            
            <div className="bg-slate-900 p-8 rounded-xl border border-slate-700 hover:border-[#fd5a27]/50 transition-all duration-300 hover:shadow-lg hover:shadow-[#fd5a27]/20 hover:-translate-y-1 group">
              <div className="w-14 h-14 bg-[#fd5a27]/10 rounded-lg flex items-center justify-center mb-4 group-hover:bg-[#fd5a27]/20 transition-all duration-300">
                <Clock className="w-7 h-7 text-[#fd5a27] group-hover:text-[#fd5a27]/80 transition-colors duration-300" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3 group-hover:text-[#fd5a27] transition-colors duration-300">24-Hour Processing</h3>
              <p className="text-gray-300">
                We process your trademark application swiftly within 24 hours, providing fast and efficient service without compromising quality.
              </p>
            </div>
            
            <div className="bg-slate-900 p-8 rounded-xl border border-slate-700 hover:border-[#fd5a27]/50 transition-all duration-300 hover:shadow-lg hover:shadow-[#fd5a27]/20 hover:-translate-y-1 group">
              <div className="w-14 h-14 bg-[#fd5a27]/10 rounded-lg flex items-center justify-center mb-4 group-hover:bg-[#fd5a27]/20 transition-all duration-300">
                <CheckCircle2 className="w-7 h-7 text-[#fd5a27] group-hover:text-[#fd5a27]/80 transition-colors duration-300" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3 group-hover:text-[#fd5a27] transition-colors duration-300">100% Satisfaction</h3>
              <p className="text-gray-300">
                We guarantee complete satisfaction with our services and stand behind our work with full confidence and unwavering commitment.
              </p>
            </div>
            
            <div className="bg-slate-900 p-8 rounded-xl border border-slate-700 hover:border-[#fd5a27]/50 transition-all duration-300 hover:shadow-lg hover:shadow-[#fd5a27]/20 hover:-translate-y-1 group">
              <div className="w-14 h-14 bg-[#fd5a27]/10 rounded-lg flex items-center justify-center mb-4 group-hover:bg-[#fd5a27]/20 transition-all duration-300">
                <Users className="w-7 h-7 text-[#fd5a27] group-hover:text-[#fd5a27]/80 transition-colors duration-300" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3 group-hover:text-[#fd5a27] transition-colors duration-300">Dedicated Support</h3>
              <p className="text-gray-300">
                24/7 customer support team ready to assist you at every step of your trademark journey with personalized attention.
              </p>
            </div>
            
            <div className="bg-slate-900 p-8 rounded-xl border border-slate-700 hover:border-[#fd5a27]/50 transition-all duration-300 hover:shadow-lg hover:shadow-[#fd5a27]/20 hover:-translate-y-1 group">
              <div className="w-14 h-14 bg-[#fd5a27]/10 rounded-lg flex items-center justify-center mb-4 group-hover:bg-[#fd5a27]/20 transition-all duration-300">
                <Award className="w-7 h-7 text-[#fd5a27] group-hover:text-[#fd5a27]/80 transition-colors duration-300" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3 group-hover:text-[#fd5a27] transition-colors duration-300">Proven Track Record</h3>
              <p className="text-gray-300">
                Thousands of successful trademark registrations with a high approval rate and outstanding client satisfaction scores.
              </p>
            </div>
            
            <div className="bg-slate-900 p-8 rounded-xl border border-slate-700 hover:border-[#fd5a27]/50 transition-all duration-300 hover:shadow-lg hover:shadow-[#fd5a27]/20 hover:-translate-y-1 group">
              <div className="w-14 h-14 bg-[#fd5a27]/10 rounded-lg flex items-center justify-center mb-4 group-hover:bg-[#fd5a27]/20 transition-all duration-300">
                <Target className="w-7 h-7 text-[#fd5a27] group-hover:text-white-500 transition-colors duration-300" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3 group-hover:text-[#fd5a27] transition-colors duration-300">Affordable Pricing</h3>
              <p className="text-gray-300">
                Competitive rates starting at just $45, making brand protection accessible to businesses of all sizes and budgets.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Statistics Section */}
      <section className="py-20 bg-gradient-to-br from-[#fd5a27]/90 via-[#e04a1f]/80 to-[#d0421a]/70 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-amber-500/10 to-transparent"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-white-500 font-semibold mb-2">OUR TRACK RECORD</p>
            <h2 className="text-4xl font-bold mb-4">Numbers That Prove Our Excellence</h2>
            <p className="text-white-400 max-w-2xl mx-auto">
              Our commitment to excellence is reflected in every trademark we file and every client we serve
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center bg-slate-900/20 backdrop-blur-sm rounded-xl p-8 border border-white/10 hover:bg-slate-900/30 hover:border-[#fd5a27] transition-all duration-300 hover:shadow-lg hover:shadow-[#fd5a27]/20 hover:-translate-y-1">
              <div className="text-6xl font-bold mb-2 text-amber-400">
                <CountUp end={10000} duration={2500} suffix="+" />
              </div>
              <div className="text-xl text-white-500 mb-2">Trademarks Filed</div>
              <p className="text-sm text-white-400">
                Successfully filed and registered over 10,000 trademarks for businesses across all industries nationwide.
              </p>
            </div>
            
            <div className="text-center bg-slate-900/20 backdrop-blur-sm rounded-xl p-8 border border-white/10 hover:bg-slate-900/30 hover:border-[#fd5a27] transition-all duration-300 hover:shadow-lg hover:shadow-[#fd5a27]/20 hover:-translate-y-1">
              <div className="text-6xl font-bold mb-2 text-amber-400">
                <CountUp end={98} duration={2000} suffix="%" />
              </div>
              <div className="text-xl text-white-500 mb-2">Success Rate</div>
              <p className="text-sm text-white-400">
                Our expert team achieves an exceptional 98% approval rate through meticulous preparation and comprehensive searches.
              </p>
            </div>
            
            <div className="text-center bg-slate-900/20 backdrop-blur-sm rounded-xl p-8 border border-white/10 hover:bg-slate-900/30 hover:border-[#fd5a27] transition-all duration-300 hover:shadow-lg hover:shadow-[#fd5a27]/20 hover:-translate-y-1">
              <div className="text-6xl font-bold mb-2 text-amber-400">
                <CountUp end={50} duration={1500} />
              </div>
              <div className="text-xl text-white-500 mb-2">States Covered</div>
              <p className="text-sm text-white-400">
                Providing trademark registration and brand protection services across all 50 United States.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-slate-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl lg:text-5xl font-bold text-white mb-4">
            Ready to Protect Your Brand?
          </h2>
          <p className="text-xl text-gray-300 mb-8">
            Join thousands of satisfied clients who have trusted us to protect their intellectual property. Let's secure your brand's future together.
          </p>
          <Button 
            onClick={() => setIsModalOpen(true)}
            className="bg-gradient-to-r from-[#fd5a27] to-[#e04a1f] hover:from-amber-500 hover:to-amber-600 text-white px-8 py-6 text-lg transition-all duration-300 shadow-lg hover:shadow-xl group"
          >
            Get Started Now
            <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform duration-300" />
          </Button>
        </div>
      </section>

      {/* Registration Modal */}
      <RegistrationModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </div>
  );
};

export default AboutPage;
